import { escapeHTML } from '../../utils/escape.js';

/**
 * Mapa de mesas de un área, para asignar mesas a una reserva desde el panel de
 * staff. Función de render pura: devuelve un string HTML, sin estado ni
 * efectos (mismo contrato que `ReservationRow`/`BlocksSection`).
 *
 * La posición sale de Fudo como GRILLA DISCRETA (`row`/`column`), no como
 * coordenadas en píxeles, así que el plano se dibuja con CSS grid y cada mesa
 * se ancla con `grid-row`/`grid-column`. El resultado es el plano ordenado, no
 * una copia exacta al píxel del de Fudo — es la fidelidad que el dato permite.
 *
 * Los tres estados nunca se comunican SOLO por color: la mesa asignada lleva
 * `aria-pressed`, y la ocupada va `disabled` con el nombre de quien la tiene
 * en su `aria-label`. Un plano donde el único indicio es un tono de fondo es
 * inusable para daltónicos y mudo para un lector de pantalla.
 */

/**
 * Clases por estado. Una mesa LIBRE se ve exactamente como en Fudo (relleno
 * verde menta sólido, número blanco, sombra suave, sin borde) porque el plano
 * tiene que resultarle familiar a quien ya usa Fudo todo el día. Los otros dos
 * estados se apartan de ese verde a propósito: son la información que Fudo no
 * tiene.
 */
const ESTADO_CLASSES = {
    asignada: 'bg-[#e5484d] text-white shadow-md ring-2 ring-offset-2 ring-[#e5484d] ring-offset-base',
    ocupada: 'bg-[#d7dbd8] text-white/70 shadow-none cursor-not-allowed',
    libre: 'bg-[#6dd6a0] text-white shadow-sm hover:brightness-105',
};

/**
 * Fudo `shape`: '1' REDONDA, '0' CUADRADA.
 *
 * El enum no está documentado y la intuición lo lee al revés. Se determinó
 * comparando el plano real de Fudo contra los datos sincronizados: las mesas
 * 1–6 y la 290 tienen `shape: '1'` y Fudo las dibuja redondas; la 240, las
 * 33/34 y el resto tienen `'0'` y las dibuja cuadradas.
 */
const SHAPE_CLASSES = {
    1: 'rounded-full',
    0: 'rounded-lg',
};

/**
 * Fudo `size`: 's' chica, 'l' grande.
 *
 * Las mesas NO tienen alto/ancho fijos: ocupan su celda de la grilla y se
 * mantienen cuadradas con `aspect-square`, con un tope para que un salón de
 * pocas columnas no las infle. Con medidas fijas, un salón de 12 columnas
 * excedía el ancho del modal y obligaba a scrollear el plano en vez de verlo
 * de un vistazo — que es el único motivo por el que un plano existe.
 */
const SIZE_CLASSES = {
    s: 'w-full aspect-square max-w-[2.25rem] text-[10px]',
    l: 'w-full aspect-square max-w-[3rem] text-xs',
};

/**
 * Etiqueta accesible de una mesa: dice el estado en palabras, y para una mesa
 * ocupada nombra a quién la tiene.
 * @param {object} mesa
 * @returns {string} ya escapada
 */
function ariaLabel(mesa) {
    const numero = escapeHTML(String(mesa.numero));
    if (mesa.estado === 'asignada') return `Mesa ${numero}, asignada a esta reserva`;
    if (mesa.estado === 'ocupada') {
        const duenio = mesa.ocupadaPor ? ` por ${escapeHTML(mesa.ocupadaPor)}` : '';
        return `Mesa ${numero}, ocupada en este turno${duenio}`;
    }
    return `Mesa ${numero}, libre`;
}

/**
 * Una mesa del plano.
 * @param {object} mesa
 * @returns {string}
 */
function renderMesa(mesa) {
    const ocupada = mesa.estado === 'ocupada';
    const estadoClasses = ESTADO_CLASSES[mesa.estado] ?? ESTADO_CLASSES.libre;
    // Un `shape` desconocido cae en cuadrada: es la forma mayoritaria del salón.
    const shapeClasses = SHAPE_CLASSES[mesa.shape] ?? SHAPE_CLASSES[0];
    const sizeClasses = SIZE_CLASSES[mesa.size] ?? SIZE_CLASSES.l;

    return `
        <button type="button"
            data-action="mesa-toggle"
            data-mesa-id="${escapeHTML(String(mesa.id))}"
            aria-pressed="${mesa.estado === 'asignada'}"
            aria-label="${ariaLabel(mesa)}"
            title="${ariaLabel(mesa)}"
            ${ocupada ? 'disabled' : ''}
            style="grid-row:${mesa.cssRow};grid-column:${mesa.cssCol}"
            class="flex items-center justify-center font-black leading-none transition-all ${estadoClasses} ${shapeClasses} ${sizeClasses}">
            ${escapeHTML(String(mesa.numero))}
        </button>
    `;
}

/** Leyenda: los estados también en texto, no solo en color. */
function renderLeyenda() {
    const item = (classes, label) => `
        <span class="inline-flex items-center gap-1.5">
            <span aria-hidden="true" class="h-3 w-3 rounded ${classes}"></span>
            <span>${label}</span>
        </span>
    `;
    return `
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] uppercase tracking-widest text-text-muted">
            ${item('bg-[#6dd6a0]', 'Libre')}
            ${item('bg-[#e5484d]', 'Asignada')}
            ${item('bg-[#d7dbd8]', 'Ocupada')}
        </div>
    `;
}

/**
 * Plano de mesas del área de una reserva.
 *
 * @param {{
 *   plan: { filas: number, columnas: number, mesas: Array<object> },
 *   reservationId: string
 * }} params
 * @returns {string} HTML del plano
 */
export const MesaMapa = ({ plan }) => {
    if (!plan || plan.mesas.length === 0) {
        return `
            <div class="rounded-2xl border border-dashed border-border-subtle p-6 text-center space-y-3">
                <p class="text-sm text-text-muted">Todavía no hay mesas cargadas para esta área.</p>
                <button type="button" data-action="mesas-sincronizar"
                    class="px-4 py-2 rounded-lg border border-border-subtle text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">
                    Sincronizar con Fudo
                </button>
            </div>
        `;
    }

    return `
        <div class="space-y-3">
            ${renderLeyenda()}
            <div role="group" aria-label="Plano de mesas"
                style="display:grid;grid-template-columns:repeat(${plan.columnas}, minmax(0, 1fr));grid-template-rows:repeat(${plan.filas}, auto);gap:0.375rem;justify-items:center;align-items:center">
                ${plan.mesas.map(renderMesa).join('')}
            </div>
        </div>
    `;
};
